/**
 * @fileoverview Login API call.
 * @description The one function that calls POST /identity/login, through the shared Axios client.
 * Verifies the identifier + password and opens a two-factor challenge — it never returns a
 * session; the emailed code is what verifyTwoFactorCode (two-factor.service.ts) exchanges for
 * one. The backend also takes the device ID and, when there is one, the current location on every
 * login attempt (a fraud/security signal, not user input), so this reads both from their existing
 * shared sources (device-identity.utils.ts, location.store.ts) rather than making every caller
 * assemble them. Location isn't required to sign in — it's left out when the store has none.
 * "Remember me" is handled entirely client-side (see credential-storage.utils.ts) and never
 * reaches this request body.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { httpClient } from '@/shared/api/http-client'
import { getOrCreateDeviceId } from '@/shared/lib/device/device-identity.utils'
import { useLocationStore } from '@/shared/lib/device/location.store'

export interface LoginRequest {
  identifier: string
  password: string
}

export interface LoginLocation {
  latitude: number
  longitude: number
  accuracyMeters?: number
  source: 'BROWSER' | 'NATIVE_GPS'
}

interface LoginRequestBody extends LoginRequest {
  clientDeviceId: string
  location?: LoginLocation
}

export interface LoginResponse {
  loginStatus: 'IN_PROGRESS'
  mfaRequired: true
  challengeId: string
  channel: 'EMAIL'
  destinationMasked: string
  expiresAt: string
  resendAvailableAt: string
  /** VERIFY_ACTIVATION_MFA when the account is registered but not yet activated: the emailed code
   * then activates the account (activation.service.ts) instead of completing sign-in. */
  nextStep: 'VERIFY_LOGIN_MFA' | 'VERIFY_ACTIVATION_MFA'
}

/** @description The largest location accuracy, in meters, the login endpoint accepts. */
const MAX_LOCATION_ACCURACY_METERS = 100_000

/**
 * @description Submits login credentials, along with this device's ID and, if known, location.
 * @param {LoginRequest} payload - The identifier/password entered on the login form.
 * @returns {Promise<LoginResponse>} The opened two-factor challenge, once credentials check out.
 */
export async function loginRequest(payload: LoginRequest): Promise<LoginResponse> {
  const { coordinates } = useLocationStore.getState()

  const body: LoginRequestBody = {
    ...payload,
    clientDeviceId: getOrCreateDeviceId(),
    ...(coordinates && {
      location: {
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        // The API rejects the whole login (400) for an accuracy over 100 km, which a browser's
        // IP-based fallback can report; the field is optional, so leave it out instead.
        ...(coordinates.accuracy <= MAX_LOCATION_ACCURACY_METERS && {
          accuracyMeters: coordinates.accuracy,
        }),
        source: 'BROWSER',
      },
    }),
  }

  const { data } = await httpClient.post<LoginResponse>('/identity/login', body)
  return data
}
